import { useMemo } from 'react';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { useAppSelector } from './hooks';
import { companyStore } from 'features/company/stores/company.slice';
import { stripeStore } from 'features/checkout/stores/stripe.slice';
import { stripeDefaultOptions } from 'features/checkout/config/checkout-form.const';
import { cartStore } from 'features/cart/stores/cart.slice';
import { stripePublishableKey, stripeSandboxPublishableKey } from '../config/utils';

const useMPTStripe = () => {
   const { companyInfo } = useAppSelector(companyStore);
   const { cart } = useAppSelector(cartStore);
   const foundCartTotal = cart?.totals?.find((cartTotalItem) => cartTotalItem.name === 'Total');
   const cartTotal = foundCartTotal ? foundCartTotal.amount : 0;

   const { stripeFormModalOpen, customerId, customerSessionClientSecret, customerClientSecret } =
      useAppSelector(stripeStore);

   const stripePromise = useMemo(() => {
      const options =
         companyInfo?.finixObject?.env.startsWith('stripe') && companyInfo?.finixObject?.stripeAcct
            ? { stripeAccount: companyInfo.finixObject.stripeAcct }
            : undefined;

      return loadStripe(
         companyInfo?.finixObject?.env.startsWith('stripe_live') ? stripePublishableKey : stripeSandboxPublishableKey,
         options,
      );
   }, [companyInfo?.finixObject]);

   const stripeAddOptions = useMemo(() => {
      return companyInfo?.currency
         ? ({
              ...stripeDefaultOptions,
              currency: companyInfo?.currency ? companyInfo.currency.toLowerCase() : 'usd',
           } as StripeElementsOptions)
         : {};
   }, [companyInfo, stripeFormModalOpen]);

   const stripePayOptions = useMemo(() => {
      return customerSessionClientSecret && companyInfo?.finixObject?.stripeAcct
         ? ({
              ...stripeDefaultOptions,
              ...(customerId ? { customer: customerId } : {}),
              mode: 'payment',
              amount: Math.round(cartTotal > 0 ? cartTotal * 100 : 1),
              paymentMethodCreation: 'manual',
              currency: companyInfo?.currency ? companyInfo.currency.toLowerCase() : 'usd',
              ...(customerSessionClientSecret ? { customerSessionClientSecret } : {}),
              appearance: {
                 rules: {
                    '.p-PaymentMethodSelector': {
                       display: 'none',
                    },
                 },
              },
           } as StripeElementsOptions)
         : {};
   }, [companyInfo, customerId, customerSessionClientSecret, cartTotal]);

   const stripeListOptions = useMemo(() => {
      return customerSessionClientSecret
         ? ({
              clientSecret: customerClientSecret,
              appearance: {},
           } as StripeElementsOptions)
         : {};
   }, [companyInfo, customerClientSecret]);

   return { stripePromise, stripeAddOptions, stripePayOptions, stripeListOptions };
};

export default useMPTStripe;
